import { z } from 'zod';

// TODO: refine after Data Structure doc review
// Enrichments are append-only. Newer rows can supersede older ones via supersededBy
// so the chain of interpretations is preserved.
//
// Layer 0 deterministic enrichments (language, content_axis, source_surface,
// duplicate, moderation) and Layer 1 AI classification (sentiment, topic,
// severity) share this table; provenance on the row distinguishes them.

export const EnrichmentTypeSchema = z.enum([
  'sentiment',
  'topic',
  'severity',
  'summary',
  'translation',
  'cluster',
  'embedding',
  'language',
  'content_axis',
  'source_surface',
  'duplicate',
  'moderation',
]);
export type EnrichmentType = z.infer<typeof EnrichmentTypeSchema>;

// --- Layer 1 (Haiku classification) ----------------------------------------

// Sentiment on a 1-5 scale with a confidence score, per the categorisation
// pipeline doc. Replaces the earlier label + (-1..1) score shape, which was
// never populated in production.
export const SentimentEnrichmentSchema = z.object({
  type: z.literal('sentiment'),
  score: z.number().int().min(1).max(5),
  confidence: z.number().min(0).max(1),
});

export const TopicEnrichmentSchema = z.object({
  type: z.literal('topic'),
  topics: z.array(z.string().min(1)).min(1),
  confidence: z.number().min(0).max(1),
});

export const SeverityEnrichmentSchema = z.object({
  type: z.literal('severity'),
  label: z.enum(['blocker', 'major', 'minor', 'cosmetic', 'praise']),
  confidence: z.number().min(0).max(1),
});

export const SummaryEnrichmentSchema = z.object({
  type: z.literal('summary'),
  text: z.string().min(1),
});

export const TranslationEnrichmentSchema = z.object({
  type: z.literal('translation'),
  language: z.string().min(2),
  text: z.string().min(1),
});

export const ClusterEnrichmentSchema = z.object({
  type: z.literal('cluster'),
  clusterId: z.string().uuid(),
  similarity: z.number().min(0).max(1),
});

export const EmbeddingEnrichmentSchema = z.object({
  type: z.literal('embedding'),
  model: z.string().min(1),
  dimensions: z.number().int().positive(),
});

// --- Layer 0 (deterministic enrichments) -----------------------------------

// Detected language as a short code (ISO 639-3, or 'und' when undetermined).
export const LanguageEnrichmentSchema = z.object({
  type: z.literal('language'),
  language: z.string().min(2),
  confidence: z.number().min(0).max(1),
});

// Routes infrastructure / third-party content off the game-feedback track.
export const ContentAxisEnrichmentSchema = z.object({
  type: z.literal('content_axis'),
  axis: z.enum(['game', 'infrastructure', 'third_party']),
  matchedKeywords: z.array(z.string()).default([]),
});

// Which surface the record came from; drives downstream surface weighting.
export const SourceSurfaceEnrichmentSchema = z.object({
  type: z.literal('source_surface'),
  surface: z.enum([
    'forum_thread',
    'chat_message',
    'help_thread',
    'playtest_response',
    'form_submission',
  ]),
});

// Exact-duplicate detection: this record repeats an earlier one from the
// same author. The original carries the authoritative enrichments.
export const DuplicateEnrichmentSchema = z.object({
  type: z.literal('duplicate'),
  originalFeedbackId: z.string().uuid(),
  contentHash: z.string().min(1),
});

// Spam / profanity screen. Records flagged here are gated out of the AI
// classification call.
export const ModerationEnrichmentSchema = z.object({
  type: z.literal('moderation'),
  isSpam: z.boolean(),
  isProfane: z.boolean(),
  matched: z.array(z.string()).default([]),
});

export const EnrichmentPayloadSchema = z.discriminatedUnion('type', [
  SentimentEnrichmentSchema,
  TopicEnrichmentSchema,
  SeverityEnrichmentSchema,
  SummaryEnrichmentSchema,
  TranslationEnrichmentSchema,
  ClusterEnrichmentSchema,
  EmbeddingEnrichmentSchema,
  LanguageEnrichmentSchema,
  ContentAxisEnrichmentSchema,
  SourceSurfaceEnrichmentSchema,
  DuplicateEnrichmentSchema,
  ModerationEnrichmentSchema,
]);
export type EnrichmentPayload = z.infer<typeof EnrichmentPayloadSchema>;

export const EnrichmentSchema = z.object({
  id: z.string().uuid(),
  feedbackId: z.string().uuid(),
  type: EnrichmentTypeSchema,
  payload: EnrichmentPayloadSchema,
  modelName: z.string().nullable(),
  modelVersion: z.string().nullable(),
  supersededBy: z.string().uuid().nullable(),
  createdAt: z.string().datetime({ offset: true }),
});
export type Enrichment = z.infer<typeof EnrichmentSchema>;
